import * as React from 'react';
import { useState, useMemo, useCallback, useEffect } from 'react';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { cn } from '@/lib/utils';
import type { LucideProps } from 'lucide-react';
import dynamicIconImports from 'lucide-react/dynamicIconImports';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { iconsData } from '@/constants/utm-icon-data';
import { useVirtualizer, type VirtualItem } from '@tanstack/react-virtual';
import { Skeleton } from '@/components/ui/skeleton';
import Fuse from 'fuse.js';
import { useDebounceValue } from 'usehooks-ts';
import { Upload } from 'lucide-react';
import { uploadTemporaryFile } from '@/services/utm/utm-api';
import { buildUtmApiUrl } from './utm-backend-url';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export type IconData = (typeof iconsData)[number];
export type IconName = keyof typeof dynamicIconImports;

interface IconPickerProps {
  iconType?: 'BUILTIN' | 'SVG' | 'IMAGE_URL' | 'S3_OBJECT';
  iconValue?: string;
  onChange: (type: 'BUILTIN' | 'SVG' | 'IMAGE_URL' | 'S3_OBJECT', value: string) => void;
  searchable?: boolean;
  searchPlaceholder?: string;
  triggerPlaceholder?: string;
  iconsList?: IconData[];
  categorized?: boolean;
  modal?: boolean;
}

// ---------------------------------------------------------------------------
// Small helpers
// ---------------------------------------------------------------------------

const IconRenderer = React.memo(({ name }: { name: IconName }) => {
  return <Icon name={name} />;
});
IconRenderer.displayName = 'IconRenderer';

const IconsColumnSkeleton = () => {
  return (
    <div className="flex flex-col gap-2 w-full">
      <Skeleton className="h-4 w-1/2 rounded-md" />
      <div className="grid grid-cols-5 gap-2 w-full">
        {Array.from({ length: 40 }).map((_, i) => (
          <Skeleton key={i} className="h-10 w-10 rounded-md" />
        ))}
      </div>
    </div>
  );
};

// ---------------------------------------------------------------------------
// Hook: lazy-load & filter the built-in icons list
// ---------------------------------------------------------------------------

const useIconsData = () => {
  const [icons, setIcons] = useState<IconData[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    let isMounted = true;

    const loadIcons = async () => {
      setIsLoading(true);

      const { iconsData: loadedIcons } = await import('@/constants/utm-icon-data');
      if (isMounted) {
        setIcons(
          loadedIcons.filter((icon: IconData) => {
            return icon.name in dynamicIconImports;
          }),
        );
        setIsLoading(false);
      }
    };

    loadIcons();

    return () => {
      isMounted = false;
    };
  }, []);

  return { icons, isLoading };
};

// ---------------------------------------------------------------------------
// Main component
// ---------------------------------------------------------------------------

const UtmIconPicker = ({
  iconType = 'BUILTIN',
  iconValue,
  onChange,
  searchable = true,
  searchPlaceholder = 'Search for an icon...',
  triggerPlaceholder = 'Select an icon',
  iconsList,
  categorized = true,
  modal = true,
}: IconPickerProps) => {
  const [activeTab, setActiveTab] = useState<'builtin' | 'custom'>('builtin');
  const [selectedIcon, setSelectedIcon] = useState<IconName | undefined>(
    iconType === 'BUILTIN' ? (iconValue as IconName) : undefined,
  );
  const [isOpen, setIsOpen] = useState(false);
  const [search, setSearch] = useDebounceValue('', 100);
  const [isPopoverVisible, setIsPopoverVisible] = useState(false);
  const [uploadError, setUploadError] = useState<string | undefined>();
  const [uploading, setUploading] = useState(false);
  const [customIconUrl, setCustomIconUrl] = useState<string | undefined>();
  const { icons } = useIconsData();
  const [isLoading, setIsLoading] = useState(true);

  // Sync selectedIcon and customIconUrl state with iconValue prop changes
  useEffect(() => {
    if (iconType === 'BUILTIN' && iconValue) {
      setSelectedIcon(iconValue as IconName);
      setCustomIconUrl(undefined);
    } else if (iconType === 'S3_OBJECT' && iconValue) {
      setSelectedIcon(undefined);
      setCustomIconUrl(
        iconValue.startsWith('http://') || iconValue.startsWith('https://')
          ? iconValue
          : buildUtmApiUrl(`/files/serve-by-key/${encodeURIComponent(iconValue)}?bucket=public`),
      );
    }
  }, [iconType, iconValue]);

  const iconsToUse = useMemo(() => iconsList || icons, [iconsList, icons]);

  const fuseInstance = useMemo(() => {
    return new Fuse(iconsToUse, {
      keys: ['name', 'tags', 'categories'],
      threshold: 0.3,
      ignoreLocation: true,
      includeScore: true,
    });
  }, [iconsToUse]);

  const filteredIcons = useMemo(() => {
    if (search.trim() === '') {
      return iconsToUse;
    }

    const results = fuseInstance.search(search.toLowerCase().trim());
    return results.map((result) => result.item);
  }, [search, iconsToUse, fuseInstance]);

  const categorizedIcons = useMemo(() => {
    if (!categorized || search.trim() !== '') {
      return [{ name: 'All Icons', icons: filteredIcons }];
    }

    const categories = new Map<string, IconData[]>();

    filteredIcons.forEach((icon) => {
      if (icon.categories && icon.categories.length > 0) {
        icon.categories.forEach((category) => {
          if (!categories.has(category)) {
            categories.set(category, []);
          }
          categories.get(category)!.push(icon);
        });
      } else {
        const category = 'Other';
        if (!categories.has(category)) {
          categories.set(category, []);
        }
        categories.get(category)!.push(icon);
      }
    });

    return Array.from(categories.entries())
      .map(([name, icons]) => ({ name, icons }))
      .sort((a, b) => a.name.localeCompare(b.name));
  }, [filteredIcons, categorized, search]);

  const virtualItems = useMemo(() => {
    const items: Array<{
      type: 'category' | 'row';
      categoryIndex: number;
      rowIndex?: number;
      icons?: IconData[];
    }> = [];

    categorizedIcons.forEach((category, categoryIndex) => {
      items.push({ type: 'category', categoryIndex });

      const rows = [];
      for (let i = 0; i < category.icons.length; i += 5) {
        rows.push(category.icons.slice(i, i + 5));
      }

      rows.forEach((rowIcons, rowIndex) => {
        items.push({
          type: 'row',
          categoryIndex,
          rowIndex,
          icons: rowIcons,
        });
      });
    });

    return items;
  }, [categorizedIcons]);

  const categoryIndices = useMemo(() => {
    const indices: Record<string, number> = {};

    virtualItems.forEach((item, index) => {
      if (item.type === 'category') {
        indices[categorizedIcons[item.categoryIndex].name] = index;
      }
    });

    return indices;
  }, [virtualItems, categorizedIcons]);

  const parentRef = React.useRef<HTMLDivElement>(null);

  const virtualizer = useVirtualizer({
    count: virtualItems.length,
    getScrollElement: () => parentRef.current,
    estimateSize: (index) => (virtualItems[index].type === 'category' ? 25 : 40),
    paddingEnd: 2,
    gap: 10,
    overscan: 5,
  });

  // -------------------------------------------------------------------------
  // Callbacks
  // -------------------------------------------------------------------------

  const handleValueChange = useCallback(
    (icon: IconName) => {
      setSelectedIcon(icon);
      onChange('BUILTIN', icon);
    },
    [onChange],
  );

  const handleOpenChange = useCallback(
    (newOpen: boolean) => {
      setSearch('');
      setIsOpen(newOpen);
      setIsPopoverVisible(newOpen);

      if (newOpen) {
        setTimeout(() => {
          virtualizer.measure();
          setIsLoading(false);
        }, 1);
      }
    },
    [virtualizer, setSearch],
  );

  const handleIconClick = useCallback(
    (iconName: IconName) => {
      handleValueChange(iconName);
      setIsOpen(false);
      setSearch('');
    },
    [handleValueChange, setSearch],
  );

  const handleSearchChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      setSearch(e.target.value);

      if (parentRef.current) {
        parentRef.current.scrollTop = 0;
      }

      virtualizer.scrollToOffset(0);
    },
    [virtualizer, setSearch],
  );

  const scrollToCategory = useCallback(
    (categoryName: string) => {
      const categoryIndex = categoryIndices[categoryName];

      if (categoryIndex !== undefined && virtualizer) {
        virtualizer.scrollToIndex(categoryIndex, {
          align: 'start',
          behavior: 'smooth',
        });
      }
    },
    [categoryIndices, virtualizer],
  );

  const handleFileUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setUploading(true);
    setUploadError(undefined);

    try {
      const result = await uploadTemporaryFile(file);

      // Store the URL for preview
      setCustomIconUrl(result.url);
      onChange('S3_OBJECT', result.iconValue);
      setActiveTab('custom'); // Stay on custom tab to show preview
    } catch (error) {
      setUploadError(error instanceof Error ? error.message : 'Failed to upload icon');
    } finally {
      setUploading(false);
    }
  };

  // -------------------------------------------------------------------------
  // Render helpers
  // -------------------------------------------------------------------------

  const categoryButtons = useMemo(() => {
    if (!categorized || search.trim() !== '') return null;

    return categorizedIcons.map((category: { name: string; icons: IconData[] }) => (
      <Button
        key={category.name}
        type="button"
        variant="outline"
        size="sm"
        className="text-xs"
        onClick={(e: React.MouseEvent) => {
          e.preventDefault();
          e.stopPropagation();
          scrollToCategory(category.name);
        }}
      >
        {category.name.charAt(0).toUpperCase() + category.name.slice(1)}
      </Button>
    ));
  }, [categorizedIcons, scrollToCategory, categorized, search]);

  const renderIcon = useCallback(
    (icon: IconData) => (
      <TooltipProvider key={icon.name}>
        <Tooltip>
          <TooltipTrigger
            className={cn(
              'p-2 rounded-md border border-transparent bg-transparent hover:border-border hover:bg-accent transition',
              'flex items-center justify-center text-foreground',
            )}
            onClick={(e: React.MouseEvent<HTMLElement>) => {
              e.preventDefault();
              e.stopPropagation();
              handleIconClick(icon.name as IconName);
            }}
            onMouseDown={(e: React.MouseEvent<HTMLElement>) => e.preventDefault()}
          >
            <IconRenderer name={icon.name as IconName} />
          </TooltipTrigger>
          <TooltipContent>
            <p>{icon.name}</p>
          </TooltipContent>
        </Tooltip>
      </TooltipProvider>
    ),
    [handleIconClick],
  );

  const renderVirtualContent = useCallback(() => {
    if (filteredIcons.length === 0) {
      return <div className="text-center text-muted-foreground">No icon found</div>;
    }

    return (
      <div
        className="relative w-full overscroll-contain"
        style={{
          height: `${virtualizer.getTotalSize()}px`,
        }}
      >
        {virtualizer.getVirtualItems().map((virtualItem: VirtualItem) => {
          const item = virtualItems[virtualItem.index];

          if (!item) return null;

          const itemStyle = {
            position: 'absolute' as const,
            top: 0,
            left: 0,
            width: '100%',
            height: `${virtualItem.size}px`,
            transform: `translateY(${virtualItem.start}px)`,
          };

          if (item.type === 'category') {
            return (
              <div key={virtualItem.key} style={itemStyle} className="top-0 bg-card z-10">
                <h3 className="font-medium text-sm capitalize text-foreground">
                  {categorizedIcons[item.categoryIndex].name}
                </h3>
                <div className="h-[1px] bg-muted w-full" />
              </div>
            );
          }

          return (
            <div key={virtualItem.key} data-index={virtualItem.index} style={itemStyle}>
              <div className="grid grid-cols-5 gap-2 w-full">
                {item.icons!.map(renderIcon)}
              </div>
            </div>
          );
        })}
      </div>
    );
  }, [virtualizer, virtualItems, categorizedIcons, filteredIcons, renderIcon]);

  React.useEffect(() => {
    if (isPopoverVisible) {
      setIsLoading(true);
      const timer = setTimeout(() => {
        setIsLoading(false);
        virtualizer.measure();
      }, 10);

      const resizeObserver = new ResizeObserver(() => {
        virtualizer.measure();
      });

      if (parentRef.current) {
        resizeObserver.observe(parentRef.current);
      }

      return () => {
        clearTimeout(timer);
        resizeObserver.disconnect();
      };
    }
  }, [isPopoverVisible, virtualizer]);

  // -------------------------------------------------------------------------
  // Render
  // -------------------------------------------------------------------------

  return (
    <div className="space-y-4">
      {/* Tab switcher */}
      <div className="flex space-x-2 border-b border-border">
        <button
          type="button"
          onClick={() => setActiveTab('builtin')}
          className={`px-4 py-2 text-sm font-medium transition-colors ${
            activeTab === 'builtin'
              ? 'text-foreground border-b-2 border-primary'
              : 'text-muted-foreground hover:text-accent-foreground'
          }`}
        >
          Built-in Icons
        </button>
        <button
          type="button"
          onClick={() => setActiveTab('custom')}
          className={`px-4 py-2 text-sm font-medium transition-colors ${
            activeTab === 'custom'
              ? 'text-foreground border-b-2 border-primary'
              : 'text-muted-foreground hover:text-accent-foreground'
          }`}
        >
          Custom Upload
        </button>
      </div>

      {/* Built-in icon grid with popover */}
      {activeTab === 'builtin' && (
        <Popover open={isOpen} onOpenChange={handleOpenChange} modal={modal}>
          <PopoverTrigger asChild>
            <Button type="button" variant="outline" className="w-full justify-start">
              {selectedIcon ? (
                <>
                  <Icon name={selectedIcon} /> {selectedIcon}
                </>
              ) : customIconUrl ? (
                <>
                  <img src={customIconUrl} alt="Custom icon" className="w-5 h-5 object-contain" />{' '}
                  Custom Icon
                </>
              ) : (
                triggerPlaceholder
              )}
            </Button>
          </PopoverTrigger>
          <PopoverContent className="w-64 p-2">
            {searchable && (
              <Input
                placeholder={searchPlaceholder}
                onChange={handleSearchChange}
                className="mb-2"
              />
            )}
            {categorized && search.trim() === '' && (
              <div className="flex flex-row gap-1 mt-2 overflow-x-auto pb-2">{categoryButtons}</div>
            )}
            <div
              ref={parentRef}
              className="max-h-60 overflow-auto"
              style={{ scrollbarWidth: 'thin' }}
            >
              {isLoading ? <IconsColumnSkeleton /> : renderVirtualContent()}
            </div>
          </PopoverContent>
        </Popover>
      )}

      {/* Custom upload tab */}
      {activeTab === 'custom' && (
        <div className="space-y-4">
          {customIconUrl ? (
            <div className="space-y-4">
              <div className="border border-border rounded-lg p-6 bg-muted">
                <p className="text-sm text-muted-foreground mb-3">Current Custom Icon:</p>
                <div className="flex items-center justify-center p-4 bg-card rounded-lg">
                  <img
                    src={customIconUrl}
                    alt="Custom icon preview"
                    className="max-w-[100px] max-h-[100px] object-contain"
                  />
                </div>
              </div>
              <div className="border-2 border-dashed border-border rounded-lg p-6 text-center">
                <label className="cursor-pointer">
                  <input
                    type="file"
                    accept="image/*"
                    onChange={handleFileUpload}
                    className="hidden"
                    disabled={uploading}
                  />
                  <span className="text-sm text-foreground hover:text-accent-foreground transition-colors">
                    {uploading ? 'Uploading...' : 'Upload different icon'}
                  </span>
                </label>
              </div>
            </div>
          ) : (
            <div className="border-2 border-dashed border-border rounded-lg p-8 text-center">
              <Upload className="mx-auto text-muted-foreground mb-3" size={40} />
              <label className="cursor-pointer">
                <input
                  type="file"
                  accept="image/*"
                  onChange={handleFileUpload}
                  className="hidden"
                  disabled={uploading}
                />
                <span className="text-sm text-foreground hover:text-accent-foreground transition-colors">
                  {uploading ? 'Uploading...' : 'Click to upload custom icon'}
                </span>
              </label>
              <p className="text-xs text-muted-foreground mt-2">Supports PNG, SVG, JPEG</p>
            </div>
          )}

          {uploadError && (
            <div className="p-3 bg-red-500/10 border border-red-500/50 rounded text-sm text-destructive">
              {uploadError}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

// ---------------------------------------------------------------------------
// Icon re-export (used by UtmSidebar and other consumers)
// ---------------------------------------------------------------------------

interface IconProps extends Omit<LucideProps, 'ref'> {
  name: IconName;
}

const Icon = ({ name, ...props }: IconProps) => {
  const importer = dynamicIconImports[name];
  const LazyIcon = useMemo(
    () => (importer ? React.lazy(importer as () => Promise<{ default: React.ComponentType<LucideProps> }>) : null),
    [importer],
  );

  if (!LazyIcon) return null;

  return (
    <React.Suspense fallback={<span className="inline-block h-4 w-4" />}>
      <LazyIcon {...props} />
    </React.Suspense>
  );
};

export default UtmIconPicker;
export { Icon };
